import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { toast } from 'sonner';
import { FileText, Plus, Search, RefreshCw, Eye, CircleDot, PencilRuler, QrCode } from 'lucide-react';
import { getProductTypeName, UNCLASSIFIED } from '@/lib/constants';
import { downloadJobCardQr } from '@/lib/jobCardQr';
import { usePlyChangeHandler, PlyChangeDialog } from '@/components/DynamicFieldRenderer';
import StandaloneCreateDialog from './JobCards/StandaloneCreateDialog';
import { formatDate, isOverdue } from './JobCards/constants';

import { Screen, Pane, CommandBar, StatStrip, DataGrid, CellButton } from '@/components/data';

/**
 * Every job card in one grid.
 *
 * Five metric cards, a filter card and a card/list toggle used to sit above nine
 * visible rows. The counts are a strip that filters, the filters are a single
 * row, and the card view is gone — it showed three cards per row and nothing the
 * table does not, which is not a view, it is fewer rows.
 *
 * A card's completion and its production stage are the two things that decide
 * whether it can run, so they stay as columns rather than being something you
 * open the card to discover.
 *
 * The strip is three lists rather than five counts of the same one: Active is
 * the working list — every card whose job is not finished, whatever its fill
 * state or stage — Not filled narrows it to the cards nobody has even said what
 * the job is, and Completed takes the finished work off the working list. How
 * full a card is stays a filter within the list, not a list of its own.
 */

const COMPLETION_OPTIONS = [
  ['all', 'Any completion'],
  ['incomplete', 'Incomplete'],
  ['partial', 'Partially filled'],
  ['complete', 'Complete'],
];

const PRIORITY_TEXT = { high: 'text-red-600', medium: 'text-amber-600', low: 'text-emerald-600' };

export default function JobCardsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [jobCards, setJobCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, incomplete: 0, partial: 0, complete: 0, in_production: 0 });
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });

  const [searchQuery, setSearchQuery] = useState('');
  const [productTypeFilter, setProductTypeFilter] = useState('all');
  const [completionFilter, setCompletionFilter] = useState('all');
  const [tab, setTab] = useState('active');

  const [productConfigs, setProductConfigs] = useState([]);

  const [standaloneDialog, setStandaloneDialog] = useState(false);
  const [standaloneForm, setStandaloneForm] = useState({
    name: '', product_type_code: '', specifications: {},
    expected_delivery_date: '', priority: 'medium', catalog_item_id: '',
  });
  const [standaloneFields, setStandaloneFields] = useState([]);
  const [standaloneCreating, setStandaloneCreating] = useState(false);
  const [catalogItems, setCatalogItems] = useState([]);

  const canCreate = ['admin', 'supervisor', 'designer'].some(
    role => (user?.roles || [user?.role]).includes(role)
  );

  const fetchJobCards = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: pagination.page, limit: pagination.limit, tab };
      if (productTypeFilter !== 'all') params.product_type = productTypeFilter;
      if (completionFilter !== 'all') params.completion = completionFilter;
      if (searchQuery) params.search = searchQuery;

      const res = await api.get('/job-cards', { params });
      setJobCards(res.data.job_cards || []);
      setStats(res.data.stats || {});
      setPagination(prev => ({ ...prev, total: res.data.total, totalPages: res.data.total_pages }));
    } catch {
      toast.error('Failed to load job cards');
    }
    setLoading(false);
  }, [pagination.page, pagination.limit, productTypeFilter, completionFilter, searchQuery, tab]);

  useEffect(() => { fetchJobCards(); }, [fetchJobCards]);

  useEffect(() => {
    api.get('/product-configs')
      .then(res => setProductConfigs(res.data.configs || []))
      .catch(() => setProductConfigs([]));
  }, []);

  const {
    plyDialog: standalonePlyDialog, handlePlyChange: standaloneHandlePlyChange,
    confirmPlyChange: standaloneConfirmPly, cancelPlyChange: standaloneCancelPly,
  } = usePlyChangeHandler(standaloneForm.specifications, (fn) => {
    setStandaloneForm(prev => ({
      ...prev,
      specifications: typeof fn === 'function' ? fn(prev.specifications) : fn,
    }));
  });

  useEffect(() => {
    if (!standaloneForm.product_type_code) { setStandaloneFields([]); return; }
    api.get(`/product-configs/${standaloneForm.product_type_code}/job-card-fields`)
      .then(res => setStandaloneFields(res.data?.fields || []))
      .catch(() => setStandaloneFields([]));
  }, [standaloneForm.product_type_code]);

  const loadCatalogItems = async () => {
    try {
      const res = await api.get('/catalog/items?limit=500');
      setCatalogItems(res.data?.items || []);
    } catch { setCatalogItems([]); }
  };

  const handleCreateStandalone = async () => {
    if (!standaloneForm.name.trim()) { toast.error('Job card name is required'); return; }
    if (!standaloneForm.product_type_code) { toast.error('Product type is required'); return; }
    setStandaloneCreating(true);
    try {
      const res = await api.post('/job-cards/standalone', {
        name: standaloneForm.name.trim(),
        product_type_code: standaloneForm.product_type_code,
        specifications: standaloneForm.specifications,
        expected_delivery_date: standaloneForm.expected_delivery_date || null,
        priority: standaloneForm.priority || 'medium',
        catalog_item_id: standaloneForm.catalog_item_id || null,
      });
      toast.success('Standalone job card created');
      setStandaloneDialog(false);
      setStandaloneForm({
        name: '', product_type_code: '', specifications: {},
        expected_delivery_date: '', priority: 'medium', catalog_item_id: '',
      });
      fetchJobCards();
      if (res.data?.job_card_id) navigate(`/job-card/${res.data.job_card_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create standalone job card');
    }
    setStandaloneCreating(false);
  };

  const handleCatalogSelect = (catItem) => {
    setStandaloneForm(prev => ({
      ...prev,
      name: prev.name || catItem.name,
      product_type_code: catItem.product_type,
      specifications: { ...catItem.job_card_fields },
      catalog_item_id: catItem.id,
    }));
    toast.success(`Specs loaded from catalog: ${catItem.name}`);
  };

  const columns = useMemo(() => ([
    {
      key: 'product',
      header: 'Product',
      width: 'minmax(11rem,1.3fr)',
      render: (jc) => (
        <>
          <span className="block truncate font-semibold text-slate-900">
            {jc.item_name || jc.product_subtype || jc.standalone_name || '—'}
          </span>
          <span className={`block truncate text-[11px] leading-tight ${
            jc.product_type_code === UNCLASSIFIED ? 'font-medium text-brand-gold-deep' : 'text-slate-400'
          }`}>
            {getProductTypeName(jc.product_type_code, true) || 'Type not set'}
          </span>
        </>
      ),
    },
    {
      key: 'customer',
      header: 'Customer / Job',
      width: 'minmax(12rem,1.2fr)',
      render: (jc) => (
        <>
          <span className="block truncate text-slate-700">
            {jc.is_standalone ? (jc.standalone_name || jc.item_name || '—') : (jc.customer_name || 'Unknown')}
          </span>
          <span className="block truncate text-[11px] leading-tight text-slate-400">
            {jc.is_standalone ? 'Standalone' : (jc.firm_name || '—')}
          </span>
        </>
      ),
    },
    {
      key: 'order_display_id',
      mobile: false,
      header: 'Order',
      width: '9.5rem',
      render: (jc) => jc.is_standalone ? (
        <span className="text-slate-400">—</span>
      ) : (
        <span className="block truncate font-mono text-[11px] text-brand-blue">{jc.order_display_id}</span>
      ),
    },
    {
      key: 'line_item_quantity',
      mobile: false,
      header: 'Qty',
      align: 'right',
      width: '5.5rem',
      render: (jc) => <span className="font-medium">{jc.line_item_quantity?.toLocaleString() || '—'}</span>,
      total: (rows) => rows.reduce((s, jc) => s + (jc.line_item_quantity || 0), 0).toLocaleString(),
    },
    {
      key: 'completion_percentage',
      header: 'Card',
      width: '6rem',
      // how full a card is says nothing about whether the job can run — it
      // counts fields, and most of them are blank on purpose. The one state
      // worth a column is a card nobody has written on at all.
      render: (jc) => (jc.completion_percentage || 0) === 0 ? (
        <span className="text-[11px] font-medium text-red-600">Not started</span>
      ) : null,
    },
    {
      key: 'current_stage',
      header: 'Production',
      width: '10rem',
      render: (jc) => (
        <>
          {jc.current_stage ? (
            <span className="flex items-center gap-1 truncate text-[12px] text-slate-700">
              <CircleDot className="h-3 w-3 shrink-0 text-blue-500" />
              {jc.current_stage.stage_name}
            </span>
          ) : (
            <span className="text-[12px] text-slate-400">
              {jc.line_item_status === 'completed' ? 'Completed' : 'Not started'}
            </span>
          )}
          <span className="block text-[10px] leading-tight text-slate-400">
            {jc.completed_stages || 0}/{jc.total_stages || 0} stages
          </span>
        </>
      ),
    },
    {
      key: 'order_priority',
      mobile: false,
      header: 'Priority',
      width: '5.5rem',
      render: (jc) => jc.order_priority ? (
        <span className={`text-[11px] font-medium ${PRIORITY_TEXT[jc.order_priority] || 'text-slate-500'}`}>
          {jc.order_priority}
        </span>
      ) : <span className="text-slate-400">—</span>,
    },
    {
      key: 'expected_delivery_date',
      header: 'Delivery',
      width: '6.5rem',
      render: (jc) => (
        <span className={`whitespace-nowrap text-[11px] ${
          isOverdue(jc.expected_delivery_date) ? 'font-medium text-red-600' : 'text-slate-600'
        }`}>
          {formatDate(jc.expected_delivery_date)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '7rem',
      align: 'right',
      render: (jc) => (
        <span className="flex items-center justify-end gap-0.5">
          {/* the sticker that goes on the sheets — one per job, so a scan opens
              the job in front of the operator rather than the whole order */}
          <CellButton icon={QrCode} label="Download QR sticker" iconOnly
            onClick={() => downloadJobCardQr(jc.id, jc.job_card_id || jc.order_display_id)} />
          <CellButton icon={PencilRuler} label="Fill job card" iconOnly
            onClick={() => navigate(`/job-card/${jc.id}/fill`)} />
          <CellButton icon={Eye} label="Open job card" iconOnly
            onClick={() => navigate(`/job-card/${jc.id}`)} />
        </span>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [navigate]);

  return (
    <Screen data-testid="job-cards-page">
      <CommandBar
        title="Job Cards"
        subtitle={`${pagination.total || stats.total || 0} card${(pagination.total || 0) === 1 ? '' : 's'}`}
        actions={[
          { key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: fetchJobCards, disabled: loading },
          canCreate && {
            key: 'new', label: 'New Job Card', icon: Plus, tone: 'accent',
            onClick: () => setStandaloneDialog(true),
          },
        ]}
      >
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <Input
            className="h-7 pl-8 text-xs"
            placeholder="Customer, item, order…"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
          />
        </div>
      </CommandBar>

      <StatStrip
        stats={[
          { key: 'active', label: 'Active', value: stats.active || 0, tone: 'blue' },
          { key: 'unfilled', label: 'Not filled', value: stats.unfilled || 0, tone: stats.unfilled ? 'danger' : 'default' },
          { key: 'completed', label: 'Completed', value: stats.completed || 0, tone: 'good' },
        ]}
        activeKey={tab}
        onSelect={(key) => {
          // a tab is always on: clicking the current one is not a way off it
          if (!key || key === tab) return;
          setTab(key);
          setPagination(p => ({ ...p, page: 1 }));
        }}
      >
        <span className="text-[10px] uppercase tracking-wider text-slate-400">Product</span>
        <Select
          value={productTypeFilter}
          onValueChange={(v) => { setProductTypeFilter(v); setPagination(p => ({ ...p, page: 1 })); }}
        >
          <SelectTrigger className="h-7 w-44 text-xs"><SelectValue placeholder="All products" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">All products</SelectItem>
            {productConfigs.map(c => (
              <SelectItem key={c.product_type_code || c.code} value={c.product_type_code || c.code} className="text-xs">
                {c.product_type_name || c.name || c.code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={completionFilter}
          onValueChange={(v) => { setCompletionFilter(v); setPagination(p => ({ ...p, page: 1 })); }}
        >
          <SelectTrigger className="h-7 w-40 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            {COMPLETION_OPTIONS.map(([value, label]) => (
              <SelectItem key={value} value={value} className="text-xs">{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </StatStrip>

      <Pane
        title="Job cards"
        aside={`${jobCards.length} shown`}
        scroll={false}
        bodyClassName="flex flex-col"
        actions={pagination.totalPages > 1 && (
          <span className="flex items-center gap-1 text-[10px] font-normal normal-case tracking-normal text-blue-100">
            <button type="button" disabled={pagination.page <= 1}
              onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
              className="rounded px-1 hover:bg-white/20 disabled:opacity-30">Prev</button>
            {pagination.page} / {pagination.totalPages || 1}
            <button type="button" disabled={pagination.page >= pagination.totalPages}
              onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
              className="rounded px-1 hover:bg-white/20 disabled:opacity-30">Next</button>
          </span>
        )}
      >
        <DataGrid
          data-testid="job-cards-table"
          columns={columns}
          rows={jobCards}
          loading={loading}
          getRowId={(jc) => jc.id}
          onRowClick={(jc) => navigate(
            jc.product_type_code === UNCLASSIFIED ? `/job-card/${jc.id}/fill` : `/job-card/${jc.id}`
          )}
          showTotals={jobCards.length > 0}
          empty={
            <span className="flex flex-col items-center gap-2 text-slate-400">
              <FileText className="h-8 w-8 text-slate-300" />
              No job cards match these filters
            </span>
          }
        />
      </Pane>

      <StandaloneCreateDialog
        open={standaloneDialog}
        onOpenChange={setStandaloneDialog}
        form={standaloneForm}
        onNameChange={(name) => setStandaloneForm(prev => ({ ...prev, name }))}
        onProductTypeChange={(code) => setStandaloneForm(prev => ({ ...prev, product_type_code: code, specifications: {} }))}
        onPriorityChange={(priority) => setStandaloneForm(prev => ({ ...prev, priority }))}
        onDeliveryDateChange={(date) => setStandaloneForm(prev => ({ ...prev, expected_delivery_date: date }))}
        onSpecChange={(fieldName, value) => setStandaloneForm(prev => ({
          ...prev,
          specifications: { ...prev.specifications, [fieldName]: value },
        }))}
        productConfigs={productConfigs}
        fields={standaloneFields}
        creating={standaloneCreating}
        onCreate={handleCreateStandalone}
        onPlyChange={standaloneHandlePlyChange}
        catalogItems={catalogItems}
        onLoadCatalog={loadCatalogItems}
        onCatalogSelect={handleCatalogSelect}
      />

      <PlyChangeDialog open={standalonePlyDialog.open} onConfirm={standaloneConfirmPly} onCancel={standaloneCancelPly}
        oldPly={standalonePlyDialog.oldPly} newPly={standalonePlyDialog.newPly} />
    </Screen>
  );
}
